import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import backgroundMusic from "@/assets/background-music.mp3";

const AudioController = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio(backgroundMusic);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.3;

    // Try to autoplay (browsers may block this)
    const playPromise = audioRef.current.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setHasInteracted(true);
          setIsPlaying(true);
        })
        .catch(() => {
          // Autoplay was blocked, wait for user interaction
          setIsPlaying(false);
          
          const handleFirstInteraction = () => {
            if (audioRef.current && !hasInteracted) {
              audioRef.current.play();
              setIsPlaying(true);
              setHasInteracted(true);
            }
            document.removeEventListener("click", handleFirstInteraction);
          };
          
          document.addEventListener("click", handleFirstInteraction, { once: true });
        });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1, duration: 0.5 }}
      className="fixed top-24 left-4 z-50"
    >
      <Button
        variant="outline"
        size="icon"
        onClick={toggleAudio}
        className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm border-2 border-primary/20 shadow-lg hover:bg-primary hover:text-white transition-all duration-300 group"
        title={isPlaying ? "Pausar música" : "Reproduzir música"}
      >
        {isPlaying ? (
          <Volume2 className="w-5 h-5 text-primary group-hover:text-white transition-colors" />
        ) : (
          <VolumeX className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors" />
        )}
      </Button>
    </motion.div>
  );
};

export default AudioController;
